import React, { useMemo, useState, useEffect } from 'react';
import Switch from 'components/form/Switch';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Form from 'components/form';
import TextInput from 'components/form/TextInput';
import Select from 'components/form/Select';
import Button from 'components/Button';
import Box from '@mui/material/Box';
import {
  addGroupKeyword,
  updateKeyword,
  getGroupKeywords,
  updateGroup,
  deleteKeyword,
} from 'services/groups.service';
import { snack } from 'utils/toast';
import SMSComposer from 'components/composers/SMSComposer';
import useAlert from 'hooks/useAlert';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useConnectionOptions from 'hooks/useConnectionOptions';
import { htmlToText } from 'components/form/AdvanceTextEditor';

const { FormItem } = Form;

const GroupKeywordSettings = ({ group, form, hasCC }) => {
  const { validateFields, getFieldDecorator, getFieldValue, setFieldsValue } =
    form;
  const [t] = useTranslation('groups');
  const [values, setValues] = useState({
    addKeyword: false,
  });
  const [keyword, setKeyword] = useState();
  const [expanded, setExpanded] = useState(false);
  const alert = useAlert();
  const user = useSelector((state) => state.user.user);

  const connectionOptions = useConnectionOptions();

  useEffect(() => {
    getGroupKeywords(group.hash, group.account_id).then((r) => {
      const kw = r[0];
      if (kw) {
        setKeyword(kw);
        setValues({
          addKeyword: !!kw,
        });
      }
    });
  }, []);

  const toggleExpanded = () => setExpanded(!expanded);

  const onToggleKeyword = (e) => {
    const { checked } = e.target;
    if (!expanded) {
      setExpanded(true);
    }
    if (!checked && keyword) {
      alert.confirm({
        title: 'Are you sure?',
        description: 'This will remove the keyword from the group.',
        onConfirm: async () => {
          if (keyword) {
            await deleteKeyword(keyword.hash);
            setValues({
              addKeyword: false,
            });
            setKeyword(null);
            setFieldsValue({
              group_keyword: '',
              connections: [],
            });
          }
        },
      });
    } else {
      setValues({
        addKeyword: checked,
      });
    }
  };

  const onSave = async () => {
    return validateFields({ force: true }).then(async (values) => {
      try {
        const text = htmlToText(values.group_reply_text);
        if (values.group_keyword && !values.connections?.length) {
          throw new Error('Please select a number');
        }
        await updateGroup(group.hash, {
          group_reply_text: text,
          group_reply_enabled: values.group_reply_enabled ? '1' : '0',
        });
        let id = keyword?.group_keyword_id;
        const connectionIds = values.connections?.map((s) => s.value);
        if (!keyword && values.group_keyword) {
          const kw = await addGroupKeyword(
            group.group_id,
            values.group_keyword,
            connectionIds,
          );
          setKeyword(kw);
          id = kw.group_keyword_id;
          setValues({
            addKeyword: true,
          });
        } else if (keyword && values.group_keyword) {
          await updateKeyword(
            keyword.hash,
            values.group_keyword,
            connectionIds,
          );
        }
        snack.success('Keyword settings saved');
      } catch (e) {
        snack.error(e.message);
      }
    });
    try {
      const text = htmlToText(values.group_reply_text);
      if (!values.group_keyword) {
        throw new Error('Keyword is required.');
      } else if (!selections.length) {
        throw new Error('Please select a number');
      } else if (autoReplyEnabled && !text) {
        throw new Error('Auto reply text is required.');
      }
    } catch (e) {
      snack.error(e.message);
    }
  };

  const autoReplyEnabled = getFieldValue('group_reply_enabled');

  useEffect(() => {
    let message = hasCC
      ? 'Hi, thanks for texting {KEYWORD}. Please TAP BELOW and complete the form. {CC_LINK}'
      : 'Hi, thanks for texting {KEYWORD}.';
    const val = getFieldValue('group_reply_text');
    if (autoReplyEnabled && !val) {
      setFieldsValue({
        group_reply_text: message,
      });
    }
  }, [autoReplyEnabled]);

  return (
    <Box mb={3}>
      <Accordion expanded={expanded} elevation={6}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row">
            <Switch
              margin="none"
              name="addKeyword"
              onChange={onToggleKeyword}
              checked={values.addKeyword}
            />
            <Box onClick={toggleExpanded}>
              <Typography gutterBottom>
                {t('activate-group-keyword-heading', 'Activate Group Keyword')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'activate-group-keyword-desc',
                  'Send a text message with your connect card link to anyone who texts your keyword',
                )}
              </Typography>
            </Box>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Form>
            <Box mt={2}>
              <Stack direction="row" spacing={1} alignItems="baseline" mb={2}>
                <Typography>Text</Typography>
                <FormItem>
                  {getFieldDecorator('group_keyword', {
                    initialValue: keyword?.keyword,
                    normalize: (val) =>
                      val && val.trim().replace(/[^\w\d\u00C0-\u00FF]+/, ''),
                    rules: [
                      {
                        required: values.addKeyword,
                        message: t(
                          'group-from-keyword-error',
                          'Please enter Group Keyword',
                        ),
                      },
                      {
                        min: 3,
                        max: 9,
                        message: t(
                          'group-form-keyword-length-error',
                          'Keyword must be between 3 and 9 characters',
                        ),
                      },
                    ],
                  })(
                    <TextInput
                      placeholder={t(
                        'group-from-keyword-placeholder',
                        'Group Keyword',
                      )}
                      hint={t(
                        'group-keyword-hint',
                        'Should be 3 to 9 characters and NO spaces',
                      )}
                    />,
                  )}
                </FormItem>
                <Typography>To</Typography>
                <FormItem>
                  {getFieldDecorator('connections', {
                    initialValue: keyword?.connections?.map((c) => {
                      return connectionOptions.find(
                        (i) => i.value === c.conn_id,
                      );
                    }),
                    rules: [
                      {
                        required: values.addKeyword,
                        message: t(
                          'group-connection-required',
                          'Please select a number',
                        ),
                      },
                    ],
                  })(
                    <Select
                      isMulti
                      maxToShow={2}
                      isClearable={false}
                      placeholder="Assign number(s)"
                      options={connectionOptions}
                      formatOptionLabel={(option, { context }) =>
                        context === 'value' ? option.address : option.label
                      }
                    />,
                  )}
                </FormItem>
              </Stack>
              <Box my={2}>
                <FormItem>
                  {getFieldDecorator('group_reply_enabled', {
                    initialValue: group.group_reply_enabled === '1',
                    valuePropName: 'checked',
                  })(
                    <Switch
                      margin="dense"
                      label="Send Keyword Automated Reply Message"
                    />,
                  )}
                </FormItem>
                <FormItem>
                  {getFieldDecorator('group_reply_text', {
                    initialValue: group.group_reply_text,
                    rules: [
                      {
                        required: autoReplyEnabled,
                        message: 'Please enter a reply text',
                      },
                    ],
                  })(<SMSComposer id="group-reply-text" />)}
                </FormItem>
              </Box>
            </Box>
          </Form>
          <Box textAlign="right">
            <Button onClick={onSave}>{t('common:save', 'Save')}</Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default Form.create()(GroupKeywordSettings);
